import { isAppleMobileDevice, isMacWithMultiTouch } from "../../../player/src/shared/utils/browser";
import { getObjectHistoryState } from './historyState.js';

const DEFAULT_POLLING_INTERVAL = 1000; // ms
const MAX_INT = 2147483647;
const PAGE_FACADE_IFRAME_NAME = 'bam-pagefacade';

export { MAX_INT };

class PageFacade extends EventTarget {
  /**
   * Full-window iframe that lets a user browse the host site.
   * Mirrors URL/title into the host using two mechanisms:
   * 1) Polling (default, works cross-origin, but not instant)
   * 2) Script injection (same-origin only, more instant)
   *
   * Exposed as a singleton via PageFacade.open() and PageFacade.close().
   *
   * @param {Object} options
   * @param {number} [options.zIndex=2147483647]
   * @param {boolean} [options.debug=true]
   */
  constructor({ url, zIndex = MAX_INT, debug = true, attributes: iframeAttributes } = {}) {
    super();
    this._initUrl = url;
    this.zIndex = zIndex;
    this.debug = debug;
    this.iframeAttributes = iframeAttributes;

    this._onIframeLoad = this._onIframeLoad.bind(this);
    this._onIframeError = this._onIframeError.bind(this);
    this._onMessageFromIframe = this._onMessageFromIframe.bind(this);
    this._onPopState = this._onPopState.bind(this);
    this._onDocumentVisibilityChange = this._onDocumentVisibilityChange.bind(this);

    this._lastSyncedUrl = null;        // last URL we pushed to history
    this._lastEmittedUrl = null;       // last URL we emitted in 'navigate'
    this._lastEmittedTitle = null;     // last title we emitted in 'navigate'
    this._pollTimer = null;
    this.iframe = null;
  }

  static ignoreCSSClass = 'bam-pf-ignore'; // Elements with this class will not be hidden by the facade

  static open(opts) {
    if (window.__pagefacadeInstance) {
      window.__pagefacadeInstance._log('open(): instance already exists, aborting.');
      return window.__pagefacadeInstance;
    }
    const instance = new PageFacade(opts);
    window.__pagefacadeInstance = instance;
    instance._open();
    return instance;
  }

  static close() {
    if (PageFacade.isOpenedByPageFacade()) {
      window.parent.postMessage({ type: 'pagefacade:close' }, '*');
      return;
    }

    if (window.__pagefacadeInstance) {
      window.__pagefacadeInstance._close();
    }
  }

  static isOpen() {
    if (PageFacade.isOpenedByPageFacade()) return true; // Either opened by parent PageFacade
    return PageFacade.isOpenedByUs(); // Or opened by self
  }

  static isOpenedByUs() {
    return !!(window.__pagefacadeInstance && window.__pagefacadeInstance.iframe);
  }

  static isOpenedByPageFacade() {
    return window !== window.parent && window.name === PAGE_FACADE_IFRAME_NAME;
  }

  static navigateTo(url) {
    if (window.__pagefacadeInstance && window.__pagefacadeInstance.iframe) {
      if (window.__pagefacadeInstance.iframe.src === url) {
        return;
      }
      window.__pagefacadeInstance.iframe.src = url;
    }
  }

  _open() {
    if (this.iframe || PageFacade.isOpenedByPageFacade()) {
      this._log('open(): already open, aborting.');
      return;
    }

    // Save scroll position and hide scrollbars. We want the iframe to be full-window
    // and not have the background scrollable.
    // We save the scroll position so we can restore it when closing the facade. This is
    // necessary because hiding scrollbars may cause a layout shift.
    const pageXOffset = window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0;
    const pageYOffset = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.scrollPos = { x: pageXOffset, y: pageYOffset };

    // Seed history so back/forward works inside the facade.
    history.replaceState({ ...getObjectHistoryState(history.state), url: location.href }, '', location.href);
    this._lastSyncedUrl = location.href;

    // Calculate zIndexes, respecting MAX_INT limits
    let iframeZIndex = this.zIndex;
    let debugZIndex = this.zIndex + 1;
    if (debugZIndex > MAX_INT) {
      debugZIndex = MAX_INT;
      iframeZIndex = MAX_INT - 1;
    }

    // Debug overlay (shows viewport bounds)
    if (this.debug) {
      const dbg = document.createElement('div');
      dbg.setAttribute('id', 'pagefacade-debug-overlay');
      dbg.classList.add(PageFacade.ignoreCSSClass);
      Object.assign(dbg.style, {
        position: 'fixed',
        inset: 0,
        zIndex: String(debugZIndex),
        pointerEvents: 'none',
        boxShadow: 'inset 0 0 0 12px red',
        opacity: '0.3',
      });
      this._dbg = dbg;
      document.body.appendChild(dbg);
    }

    // Build iframe (invisible & non-interactive until loaded).
    this.iframe = document.createElement('iframe');
    Object.assign(this.iframe.style, {
      display: 'block',
      position: 'fixed',
      inset: 0,
      width: '100%',
      height: '100%',
      border: '0',
      margin: '0',
      padding: '0',
      zIndex: String(iframeZIndex),
      opacity: '0',
      backgroundColor: getPageBgColor(),
      pointerEvents: 'none',
    });
    this.iframe.src = this._initUrl || location.href;
    this.iframe.name = PAGE_FACADE_IFRAME_NAME;
    this.iframe.setAttribute('data-pagefacade', 'true'); // For CSS rules
    this.iframe.setAttribute('frameborder', 0);
    this.iframe.setAttribute('allowTransparency', true);
    this.iframe.setAttribute('allow', 'fullscreen; autoplay; web-share');
    if (this.iframeAttributes) {
      Object.entries(this.iframeAttributes).forEach(([k, v]) => {
        this.iframe.setAttribute(k, v);
      });
    }
    this.iframe.setAttribute('tabindex', '-1');
    this.iframe.classList.add(PageFacade.ignoreCSSClass);

    this.iframe.addEventListener('load', this._onIframeLoad);
    this.iframe.addEventListener('error', this._onIframeError);

    document.body.appendChild(this.iframe);

    window.addEventListener('message', this._onMessageFromIframe);
    window.addEventListener('popstate', this._onPopState);
    document.addEventListener('visibilitychange', this._onDocumentVisibilityChange);

    // Start polling for URL/title changes
    this._startPollingNavigationInIframe();

    // REMARK: Fix some weird rendering issues with iOS and elements with fixed positioning inside the surf iframe.
    // By specifying this styling value on page loads it can later be removed or changed by the page inside the iframe
    // and everything would work as expected, without this the UI could potentially be unusable other wise...
    if (isAppleMobileDevice() || isMacWithMultiTouch()) {
      window.setTimeout(() => {
        if (!this.iframe) return;
        try {
          const iframeDoc = this.iframe.contentDocument || this.iframe.contentWindow.document;
          if (iframeDoc?.body.style.position) {
            iframeDoc.body.style.position = 'unset';
          }
          iframeDoc.body.style.position = 'initial';
        } catch (e) {
          /* Just swallow any cross-domain errors... */
        }
      }, 100);
    }

    // Emit 'open' event (in next tick to allow listener attachment)
    setTimeout(() => {
      this.dispatchEvent(new CustomEvent('open'));
    }, 0);
    this._log('open(): facade inserted, listeners attached, polling started.');
  }

  _close() {
    if (!this.iframe) return;
    if (PageFacade.isOpenedByPageFacade()) {
      this._log('close(): not top-level, tell parent to close instead.');
      window.parent.postMessage({ type: 'pagefacade:close' }, '*');
      return;
    }

    this._stopPollingNavigationInIframe();

    this.iframe.removeEventListener('load', this._onIframeLoad);
    this.iframe.removeEventListener('error', this._onIframeError);
    window.removeEventListener('message', this._onMessageFromIframe);
    window.removeEventListener('popstate', this._onPopState);
    document.removeEventListener('visibilitychange', this._onDocumentVisibilityChange);

    if (this.iframe.parentNode) this.iframe.parentNode.removeChild(this.iframe);
    this.iframe = null;

    if (this._dbg) this._dbg.parentNode.removeChild(this._dbg);
    this._dbg = null;

    this._restoreHostPage();

    this.dispatchEvent(new CustomEvent('close'));
    this._log('close(): facade removed and state restored.');

    delete window.__pagefacadeInstance;
  }

  _onIframeLoad() {
    this._log('_onIframeLoad(): iframe loaded');

    this._disableHostPage();
    this._injectSyncScript();

    // Reveal iframe & restore scroll position inside it. But only if this is
    // the first load (not on subsequent navigations inside the iframe).
    if (!this._hasScrolledInitially) {
      this._hasScrolledInitially = true;
      try {
        this.iframe.contentWindow.scrollTo(this.scrollPos.x, this.scrollPos.y);
      } catch {} // eslint-disable-line no-empty
    }

    this.iframe.style.opacity = '1';
    this.iframe.style.pointerEvents = 'auto';

    // Initial navigate (best-effort) and prime lastEmitted to avoid double-emits.
    try {
      const href = this.iframe.contentWindow.location.href;
      const title = this.iframe.contentDocument.title;
      this._lastEmittedUrl = href;
      this._lastEmittedTitle = title;
      this.dispatchEvent(new CustomEvent('navigate', { detail: { url: href, title } }));
    } catch {
      this.dispatchEvent(new CustomEvent('navigate', { detail: { url: this.iframe.src, title: document.title } }));
    }
  }

  _onIframeError(ev) {
    this._log('_onIframeError(): iframe failed to load.', ev?.message || ev);
    this.dispatchEvent(new CustomEvent('error', { detail: ev }));
  }

  _onPopState(e) {
    const targetUrl = e?.state?.url;
    this._log('_onPopState():', targetUrl || '(no state)');
    if (!this.iframe || !targetUrl) return;

    if (!isSafeSameOriginUrl(targetUrl)) {
      this._log('_onPopState(): unsafe URL in state; ignoring.');
      return;
    }

    if (targetUrl === this._lastSyncedUrl) {
      this._log('_onPopState(): target equals last synced; skipping iframe src change.');
      return;
    }

    this.iframe.src = targetUrl;
    this._log('_onPopState(): iframe.src set ->', targetUrl);
  }

  _startPollingNavigationInIframe() {
    if (this._pollHandle) return;
    if (!this._pollingInterval) this._pollingInterval = DEFAULT_POLLING_INTERVAL;
    let iterationsBeforeReset = 20; // After this many iterations, reset to default interval.

    // Check the iframe's URL and title every _pollingInterval ms.
    // If we detect a change, update our history and title so that it seems like
    // the user is actually browsing at the top level.
    const tick = () => {
      // If we've been polling fast for a while, reset to default interval to
      // avoid excessive CPU usage in case we missed the navigation event.
      if (--iterationsBeforeReset <= 0) {
        this._log('_poll(): resetting polling interval to default.');
        this._pollingInterval = DEFAULT_POLLING_INTERVAL;
        iterationsBeforeReset = 20;
      }

      try {
        const href = this.iframe.contentWindow.location.href;
        const title = this.iframe.contentDocument.title;

        let changed = false;

        // Sync URL to history when it changes and is safe.
        if (href && href !== location.href && href !== this._lastSyncedUrl && isSafeSameOriginUrl(href)) {
          //history.pushState({ url: href }, '', href);
          history.replaceState({
            ...getObjectHistoryState(history.state),
            url: href,
            bambuserLiveShopping: true,
          }, title, href);
          this._lastSyncedUrl = href;
          changed = true;
          this._log('_poll(): history.replaceState ->', href);
        }
        // Sync title when it changes.
        if (title && title !== document.title) {
          document.title = title;
          changed = true;
          this._log('_poll(): document.title updated.');
        }

        // Emit only when either URL or title changed since last emit.
        if (changed && (href !== this._lastEmittedUrl || title !== this._lastEmittedTitle)) {
          this._lastEmittedUrl = href;
          this._lastEmittedTitle = title;
          this.dispatchEvent(new CustomEvent('navigate', { detail: { url: href, title } }));

          // Reset polling interval if it was boosted.
          this._pollingInterval = DEFAULT_POLLING_INTERVAL;
        }
      } catch {
        // Cross-origin or not ready; ignore and retry next tick.
      }

      this._pollHandle = setTimeout(tick, this._pollingInterval);
    };

    tick();
  }

  _stopPollingNavigationInIframe() {
    if (this._pollHandle) {
      clearTimeout(this._pollHandle);
      this._pollHandle = null;
      this._pollingInterval = null;
      this._log('_stopPollingNavigationInIframe(): cleared.');
    }
  }

  _onDocumentVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this._log('_onDocumentVisibilityChange(): visible; ensuring polling.');
      this._startPollingNavigationInIframe();
    } else {
      this._log('_onDocumentVisibilityChange(): hidden; stopping polling.');
      this._stopPollingNavigationInIframe();
    }
  }

  _injectSyncScript() {
    try {
      const doc = this.iframe.contentDocument;
      if (!doc || !doc.head) return false;
      if (doc.URL && doc.URL.startsWith('about:')) return false;
      if (doc.getElementById('pagefacade-sync')) return true;

      const script = doc.createElement('script');
      script.id = 'pagefacade-sync';
      script.textContent = `(${syncFn.toString()})()`;
      doc.head.appendChild(script);
      return true;
    } catch (e) {
      this._log('_injectSyncScript(): failed (likely CSP or cross-origin).', e?.message || e);
      return false;
    }
  }

  _onMessageFromIframe(e) {
    if (e.data?.source === 'react-devtools-content-script') return; // Ignore React DevTools messages

    if (!this.iframe || e.source !== this.iframe.contentWindow) return; // Not from our iframe
    const msg = e.data;

    if (msg?.type === 'pagefacade:will-navigate') {
      // The iframe is about to navigate. Temporarily boost polling frequency to
      // catch the navigation as soon as possible.
      this._log('_onMessageFromIframe(): will-navigate received; boosting polling.');
      this._stopPollingNavigationInIframe();
      this._pollingInterval = 50;
      this._startPollingNavigationInIframe();
    }

    if (msg?.type === 'pagefacade:will-navigate-cross-origin' && msg?.href) {
      // The iframe is about to navigate to a cross-origin URL.
      // That probably will break the iframe entirely, so we need to it's
      // better to navigate the host page instead (closing the facade).
      this._log('_onMessageFromIframe(): will-navigate-cross-origin received; setting iframe.src ->', msg.href);
      window.location.href = msg.href;
    }

    if (msg?.type === 'pagefacade:scroll' && typeof msg?.scrollY === 'number') {
      // The iframe scrolled. If the iframe currently has the host page loaded,
      // we should also scroll the host page to match.
      if (this.iframe.src === location.href) {
        window.scrollTo(0, msg.scrollY);
        this._log('_onMessageFromIframe(): scroll received; scrolling host to', msg.scrollY);
      }
    }

    if (msg?.type === 'pagefacade:close') {
      this._log('_onMessageFromIframe(): close received; closing.');
      this._close();
    }
  }

  _disableHostPage() {
    document.documentElement.style.setProperty('overflow', 'hidden', 'important'); // documentElement = html
    document.body.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.marginTop = '0'; // Avoid margin causing weird shifts
    // TODO: Maybe also disable margin on html?
    document.body.style.overscrollBehavior = 'none'; // Disables pull-to-refresh and overscroll glow effect

    // Inject a CSS rule to hide all top level elements except the iframe itself and
    // any elements marked with the ignoreCSSClass (e.g. the miniplayer or shoppable videos FAB).
    if (!document.getElementById('pagefacade-hide-elements')) {
      const style = document.createElement('style');
      style.id = 'pagefacade-hide-elements';
      style.textContent = `
        body > *:not(.${PageFacade.ignoreCSSClass}) {
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    document.addEventListener('touchmove', conditionallyPreventBodyScrollEvent, { passive: false });

    this._log('_disableHostPage(): scrollbars hidden.');
  }

  _restoreHostPage() {
    document.documentElement.style.setProperty('overflow', ''); // documentElement = html
    document.body.style.setProperty('overflow', '');
    document.body.style.marginTop = '';
    document.body.style.overscrollBehavior = '';

    // Remove the CSS rule that hides all top level elements
    const style = document.getElementById('pagefacade-hide-elements');
    if (style) style.remove();

    document.removeEventListener('touchmove', conditionallyPreventBodyScrollEvent, { passive: false });
    this._log('_restoreHostPage(): scrollbars restored.');
  }

  _log(...args) {
    if (this.debug) console.log('[PageFacade]', ...args);
  }
}

/**
 * Checks if a URL is same-origin and safe to use in history.pushState().
 */
const isSafeSameOriginUrl = (u) => {
  try {
    if (!u || u.startsWith('about:')) return false;
    const abs = new URL(u, location.href);
    return abs.origin === location.origin && /^(http|https):$/.test(abs.protocol);
  } catch {
    return false;
  }
};

const getPageBgColor = () => {
  const htmlBg = getComputedStyle(document.documentElement).backgroundColor;
  const bodyBg = getComputedStyle(document.body).backgroundColor;
  const pick = !isTransparent(htmlBg) ? htmlBg : (!isTransparent(bodyBg) ? bodyBg : '#fff');
  return pick || '#fff';
};

const isTransparent = (color) => {
  if (!color) return true;
  if (color === 'transparent') return true;
  const m = color.match(/^rgba?\(([^)]+)\)$/i);
  if (!m) return false;
  const parts = m[1].split(',').map(s => s.trim());
  if (parts.length === 4) {
    const a = parseFloat(parts[3]);
    return a === 0;
  }
  return false;
};

const conditionallyPreventBodyScrollEvent = (rawEvent) => {
  const e = rawEvent || window.event;
  if (!e) return false;

  // Do not prevent if the event has more than one touch
  // (usually meaning this is a multi touch gesture like pinch to zoom).
  if (e.touches?.length > 1) {
    return true;
  }

  if (e.preventDefault) {
    e.preventDefault();
  }

  return false;
};

/**
 * This function is stringified and injected into the iframe.
 * The purpose of the script is to notify the parent window
 * about navigation inside the iframe, so the parent can
 * update its history state and title accordingly.
 * It will also intercept clicks on links and notify the parent
 * if the link is a cross-origin navigation (so the parent can
 * navigate itself and close the facade).
 */
const syncFn = () => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.focus();
    });
  } else {
    window.focus();
  }

  // Notify parent window that we're about to navigate away.
  window.addEventListener('beforeunload', () => {
    try {
      window.parent.postMessage({ type: 'pagefacade:will-navigate' }, '*');
    } catch {} // eslint-disable-line no-empty
  });

  const isSafeSameOriginUrl = (u) => {
    try {
      if (!u || u.startsWith('about:')) return false;
      const abs = new URL(u, location.href);
      return abs.origin === location.origin && /^(http|https):$/.test(abs.protocol);
    } catch {
      return false;
    }
  };

  // Intercept clicks on links. If the link is same-origin, let it navigate normally.
  // If it's cross-origin, notify the parent so it can navigate instead (and close the facade).
  document.addEventListener('click', e => {
    const target = e.target instanceof Element ? e.target : null;
    const a = target?.closest('a');
    if (a?.href && !a.target) {
      if (isSafeSameOriginUrl(a.href)) {
        // Same-origin link. Let it navigate normally.
        return;
      }
      // Cross-origin link. Notify parent so it can navigate instead.
      e.preventDefault();
      try {
        window.parent.postMessage({ type: 'pagefacade:will-navigate-cross-origin', href: a.href }, '*');
      } catch {
        // If we can't reach the parent, just navigate ourselves. It will break
        // but at least we know what happened.
        location.href = a.href;
      }
    }
  });

  // Emit scroll events from the iframe to the parent. We'll use these to
  // sync the scroll position in the parent when the iframe has the same
  // URL loaded (so when the user exits the facade, the scroll position
  // is the same as where they were inside the iframe).
  const throttled = (fn, delay) => {
    let lastCall = 0;
    return (...args) => {
      const now = (new Date()).getTime();
      if (now - lastCall < delay) return;
      lastCall = now;
      return fn(...args);
    };
  };
  window.addEventListener('scroll', throttled(() => {
    try {
      window.parent.postMessage({ type: 'pagefacade:scroll', scrollY: window.scrollY }, '*');
    } catch {} // eslint-disable-line no-empty
  }, 500));
};


export default PageFacade;
